import { useState } from 'react';
import { ArrowLeft, Lock, ShieldCheck, X } from 'lucide-react';
import InlineLogin from './InlineLogin';

interface AuthRequiredModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
    onSignUp: () => void;
    /** Signed in without leaving: resume the submission straight away. */
    onAuthenticated: () => void;
    planName: string;
    subjectCount: number;
    total: number;
}

function formatNaira(value: number): string {
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0,
    }).format(value || 0);
}

/**
 * The one place the anonymous flow stops.
 *
 * A visitor can choose a plan, pick their checks and enter who they are
 * checking without an account — an account is only needed to own the
 * investigation and to pay. Everything they typed is already parked by the
 * time this appears, so the promise made here ("we've saved your details") is
 * one the flow can actually keep.
 */
export default function AuthRequiredModal({
    isOpen,
    onClose,
    onBack,
    onSignUp,
    onAuthenticated,
    planName,
    subjectCount,
    total,
}: AuthRequiredModalProps) {
    // Existing customers sign in right here rather than being navigated away,
    // which would discard anything held in memory (attached documents most of
    // all) and drop them on the dashboard instead of back at payment.
    const [showLogin, setShowLogin] = useState(false);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
                >
                    <X className="h-4 w-4 text-gray-500" />
                </button>

                {showLogin ? (
                    <InlineLogin onSuccess={onAuthenticated} onCancel={() => setShowLogin(false)} />
                ) : (
                    <>
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF3EE]">
                            <Lock className="h-5 w-5 text-[#FF6B35]" />
                        </div>

                        <h2 className="mt-4 text-lg font-bold text-gray-900">Almost there</h2>
                        <p className="mt-1.5 text-sm text-gray-600">
                            Create an account to pay and track your report. It takes a minute, and you come straight
                            back here.
                        </p>

                        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">
                                    {planName} · {subjectCount} {subjectCount === 1 ? 'person' : 'people'}
                                </span>
                                <span className="font-bold text-gray-900 tabular-nums">{formatNaira(total)}</span>
                            </div>
                        </div>

                        <p className="mt-3 flex items-start gap-1.5 text-xs text-green-700">
                            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            Your details are saved. Nothing is lost while you sign in.
                        </p>

                        <button
                            type="button"
                            onClick={onSignUp}
                            className="mt-5 w-full rounded-xl bg-[#FF6B35] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#15171C]"
                        >
                            Create an account
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowLogin(true)}
                            className="mt-2 w-full rounded-xl border border-gray-200 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                        >
                            I already have an account
                        </button>

                        <button
                            type="button"
                            onClick={onBack}
                            className="mt-4 flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to details
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
